import { ReactNode, useCallback, useEffect } from 'react'
import {
    Drawer,
    List,
    Divider,
    Theme,
    IconButton,
    Box,
    useTheme,
    styled,
    listItemTextClasses,
    listItemIconClasses,
    listItemButtonClasses,
} from '@mui/material'
import Logo from 'assets/logo.png'

import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useAppSelector } from 'store/useStoreHook'
import { toggleDrawer, setDrawer } from 'store/actions/drawer.action'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { SxProps } from '@mui/system'
import { ILinkTab, IRoute } from 'interfaces'
import MenuGroup from './MenuGroup'
import { isDarkTheme } from 'utilities/themeHelper'
import useAdminRoute from 'router/useAdminRoute'
import { AdminRouteName } from 'router/IRouteConstant'
import { isNil, values } from 'lodash-es'
import ROUTE from 'constant/route'
export const AdminNav = styled(List)<{ component?: React.ElementType }>(
    ({ theme }) => ({
        [`& .${listItemButtonClasses.root}`]: {
            paddingLeft: 16,
            paddingRight: 16,
        },
        [`& .${listItemIconClasses.root}`]: {
            fontSize: 24,
            marginRight: 20,
            minWidth: 0,
        },
        [`& .${listItemTextClasses.primary}`]: {
            textTransform: 'uppercase',
        },
        [`& .${listItemButtonClasses.selected}`]: {
            borderLeft: '0 !important',
            color: theme.palette.primary.contrastText + ' !important',
            backgroundColor: `${
                isDarkTheme(theme)
                    ? theme.palette.background.default
                    : theme.palette.primary.main
            } !important`,
        },
    }),
)
const useStyle = (theme: Theme): { [key: string]: SxProps } => {
    return {
        drawerHeader: {
            width: '284px',
            height: {
                md: theme.spacing(8),
                lg: theme.spacing(10),
                xl: theme.spacing(10),
            },
            display: 'flex',
            alignItems: 'center',
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            opacity: '1',
            position: 'fixed',
            zIndex: 11,
        },
    }
}
export interface IMenuNav extends IRoute {
    icon: ReactNode
    title: string
    /** will override from routes's requirePermission */
    requirePermission: number[]
    disabled?: boolean
    routes?: ILinkTab[]
}
export interface IMenu {
    title: string
    navs?: { [tab in AdminRouteName]?: IMenuNav }
    displayFirst?: boolean
    /** will override from navs's requirePermission */
    requirePermission?: number[]
}

const ignoreOpenedDrawerPath: string[] = [ROUTE.ORDER_DASHBOARD.valueOf()]

export default function SideNavigation() {
    const history = useHistory()
    const theme = useTheme()
    const classes = useStyle(theme)
    const openDrawer = useAppSelector((state) => state.drawer.open)
    const currentClient = useAppSelector((state) => state.client.currentClient)
    const dispatch = useDispatch()
    const drawerToggle = useCallback(() => {
        dispatch(toggleDrawer())
    }, [dispatch])
    const drawerClose = useCallback(() => {
        dispatch(setDrawer(false))
    }, [dispatch])
    const drawerOpen = useCallback(() => {
        dispatch(setDrawer(true))
    }, [dispatch])
    const { isMobileTablet, isBigScreen } = useScreenSize()

    const handleClick = (nav: IMenuNav) => {
        if (isMobileTablet) {
            drawerClose()
        }
        history.push(nav.path)
    }
    const { menus } = useAdminRoute()
    useEffect(() => {
        if (isMobileTablet) {
            drawerClose()
        } else if (
            isBigScreen &&
            !ignoreOpenedDrawerPath.includes(window?.location?.pathname ?? '')
        ) {
            drawerOpen()
        }
    }, [isMobileTablet, drawerClose, isBigScreen, drawerOpen])

    return (
        <nav style={{ flexShrink: 0, width: 284 }}>
            {openDrawer && isMobileTablet && (
                <Box
                    sx={{
                        backdropFilter: 'blur(3px)',
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1200,
                        left: '284px',
                    }}
                    onClick={drawerClose}
                ></Box>
            )}
            <Drawer variant="persistent" open={openDrawer} anchor="left">
                {openDrawer && isMobileTablet && (
                    <Box
                        sx={{
                            width: '100%',
                            backdropFilter: 'blur(3px)',
                            position: 'fixed',
                            inset: 0,
                        }}
                        onClick={drawerClose}
                    ></Box>
                )}
                <Box
                    sx={{
                        width: '284px',
                    }}
                >
                    <Box sx={classes.drawerHeader} onClick={drawerToggle}>
                        <img
                            style={{
                                width: 250,
                                opacity: 1,
                                left: 10,
                                // position: 'absolute',
                            }}
                            src={Logo}
                            alt="Logo"
                        />
                        <Box
                            width="100%"
                            display="flex"
                            justifyContent="flex-end"
                        >
                            <IconButton color="inherit">
                                {openDrawer ? (
                                    <ArrowBackIosNewIcon />
                                ) : (
                                    <ArrowForwardIosIcon />
                                )}
                            </IconButton>
                        </Box>
                    </Box>
                    <Divider
                        sx={{
                            mt: {
                                md: theme.spacing(8),
                                lg: theme.spacing(10),
                                xl: theme.spacing(10),
                            },
                        }}
                    />
                    <AdminNav>
                        {values(menus).map((menu) => {
                            const navs = values(menu.navs) ?? []
                            const hasOpenned = !isNil(
                                navs.find(
                                    (i) =>
                                        i.path === history.location.pathname ||
                                        i.routes?.find(
                                            (r) =>
                                                r.path ===
                                                history.location.pathname,
                                        ),
                                ),
                            )
                            return (
                                <MenuGroup
                                    key={`nav-menu-group-${menu.title}`}
                                    onClickMenu={handleClick}
                                    menu={menu}
                                    currentClient={currentClient}
                                    hasOpenned={hasOpenned}
                                />
                            )
                        })}
                    </AdminNav>
                </Box>
            </Drawer>
        </nav>
    )
}
